import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { shipmentBatchesAPI } from '../services/api';
import { Plus, Edit, Trash2, Save, X } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';

const ShipmentBatches = () => {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editBatch, setEditBatch] = useState(null);
  const [form, setForm] = useState({ name: '', shipment_date: '', status: 'PENDING' });
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const statusOptions = [
    { value: 'PENDING', label: 'Pending' },
    { value: 'READY', label: 'Ready' },
    { value: 'DISPATCHED', label: 'Dispatched' },
    { value: 'ENROUTE', label: 'Enroute' },
    { value: 'ARRIVED', label: 'Arrived' },
    { value: 'DELIVERED', label: 'Delivered' },
  ];

  const { data: batchesData, isLoading } = useQuery(['shipmentBatches', search, statusFilter], () => shipmentBatchesAPI.getAll({ search, status: statusFilter }));

  const createMutation = useMutation(shipmentBatchesAPI.create, {
    onSuccess: () => { setShowModal(false); setForm({ name: '', shipment_date: '', status: 'PENDING' }); queryClient.invalidateQueries('shipmentBatches'); },
    onError: (err) => setError(err.response?.data?.error || 'Failed to create batch'),
  });
  const updateMutation = useMutation(({ id, ...data }) => shipmentBatchesAPI.update(id, data), {
    onSuccess: () => { setShowModal(false); setEditBatch(null); setForm({ name: '', shipment_date: '', status: 'PENDING' }); queryClient.invalidateQueries('shipmentBatches'); },
    onError: (err) => setError(err.response?.data?.error || 'Failed to update batch'),
  });
  const deleteMutation = useMutation(shipmentBatchesAPI.delete, {
    onSuccess: () => queryClient.invalidateQueries('shipmentBatches'),
    onError: (err) => setError(err.response?.data?.error || 'Failed to delete batch'),
  });

  const openAdd = () => { setEditBatch(null); setForm({ name: '', shipment_date: '', status: 'PENDING' }); setShowModal(true); setError(''); };
  const openEdit = (batch) => { setEditBatch(batch); setForm({ name: batch.name, shipment_date: batch.shipment_date ? batch.shipment_date.slice(0, 10) : '', status: batch.status }); setShowModal(true); setError(''); };
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name) { setError('Name is required'); return; }
    if (editBatch) updateMutation.mutate({ id: editBatch.id, ...form });
    else createMutation.mutate(form);
  };
  const handleDelete = (id) => { if (window.confirm('Delete this batch?')) deleteMutation.mutate(id); };

  const batches = batchesData?.batches || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Shipment Batches</h1>
        <button className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg" onClick={openAdd}>
          <Plus className="h-4 w-4 mr-2" /> Add Batch
        </button>
      </div>
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
        <input
          type="text"
          className="border rounded px-3 py-2 w-full sm:w-64"
          placeholder="Search by batch name..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select
          className="border rounded px-3 py-2 w-full sm:w-64"
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
        >
          <option value="">All Statuses</option>
          {statusOptions.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>
      {isLoading ? <LoadingSpinner /> : (
        <table className="min-w-full bg-white border rounded-lg">
          <thead>
            <tr>
              <th className="px-4 py-2 border-b">Name</th>
              <th className="px-4 py-2 border-b">Shipment Date</th>
              <th className="px-4 py-2 border-b">Status</th>
              <th className="px-4 py-2 border-b">Created</th>
              <th className="px-4 py-2 border-b">Modified</th>
              <th className="px-4 py-2 border-b">Actions</th>
            </tr>
          </thead>
          <tbody>
            {batches.map(batch => (
              <tr key={batch.id}>
                <td className="px-4 py-2 border-b">{batch.name}</td>
                <td className="px-4 py-2 border-b">{batch.shipment_date ? batch.shipment_date.slice(0, 10) : ''}</td>
                <td className="px-4 py-2 border-b">{batch.status}</td>
                <td className="px-4 py-2 border-b">{batch.created_at ? batch.created_at.slice(0, 19).replace('T', ' ') : ''}</td>
                <td className="px-4 py-2 border-b">{batch.modified_at ? batch.modified_at.slice(0, 19).replace('T', ' ') : ''}</td>
                <td className="px-4 py-2 border-b space-x-2">
                  <button className="text-blue-600" onClick={() => openEdit(batch)}><Edit className="inline h-4 w-4" /></button>
                  <button className="text-red-600" onClick={() => handleDelete(batch.id)}><Trash2 className="inline h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold">{editBatch ? 'Edit Batch' : 'Add Batch'}</h2>
              <button onClick={() => setShowModal(false)}><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block mb-1 font-medium">Name</label>
                <input type="text" className="w-full border rounded px-3 py-2" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div>
                <label className="block mb-1 font-medium">Shipment Date</label>
                <input type="date" className="w-full border rounded px-3 py-2" value={form.shipment_date} onChange={e => setForm(f => ({ ...f, shipment_date: e.target.value }))} />
              </div>
              <div>
                <label className="block mb-1 font-medium">Status</label>
                <select className="w-full border rounded px-3 py-2" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                  {statusOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              {error && <div className="text-red-600 text-sm">{error}</div>}
              <div className="flex justify-end space-x-2">
                <button type="button" className="px-4 py-2 bg-gray-200 rounded" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded flex items-center"><Save className="h-4 w-4 mr-1" />Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShipmentBatches; 